'use client'

import { useState, useCallback } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { getBlogPosts } from '#/lib/content'
import { useCmsContent } from '#/hooks/useCmsContent'
import Container from '#/components/layout/Container'
import Section from '#/components/layout/Section'
import BlogCard from '#/components/blog/BlogCard'
import { BlurFade } from '#/components/ui/blur-fade'
import { useCms } from '#/components/cms/CmsProvider'
import EditModal from '#/components/cms/EditModal'
import EditablePageHeader from '#/components/cms/EditablePageHeader'
import type { EditField } from '#/components/cms/EditModal'
import { addPendingAdd } from '#/lib/cms-store'
import { Plus } from 'lucide-react'

import { seoMeta, canonicalLink } from '#/lib/seo'

export const Route = createFileRoute('/blog/')({
  head: () => ({
    meta: seoMeta({
      title: 'Blog',
      description:
        'Stories, recipes, and behind-the-scenes glimpses from Main Street Bread in Ferndale.',
      path: '/blog',
    }),
    links: [canonicalLink('/blog')],
  }),
  loader: async () => {
    const posts = await getBlogPosts()
    return { posts: posts.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt)) }
  },
  component: BlogListPage,
})

const newPostFields: EditField[] = [
  { name: 'title', type: 'text', label: 'Title' },
  { name: 'excerpt', type: 'textarea', label: 'Excerpt' },
  { name: 'author', type: 'text', label: 'Author' },
  { name: 'publishedAt', type: 'text', label: 'Published Date (YYYY-MM-DD)' },
  { name: 'tags', type: 'tags', label: 'Tags' },
  { name: 'image', type: 'image', label: 'Image' },
  { name: 'content', type: 'markdown', label: 'Content' },
]

function BlogListPage() {
  const data = Route.useLoaderData()
  const posts = useCmsContent('blog-posts', data.posts)
  const { isEditMode } = useCms()
  const [addOpen, setAddOpen] = useState(false)

  const handleAdd = useCallback(
    (values: Record<string, unknown>) => {
      const slug = String(values.title ?? 'untitled')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
      addPendingAdd('blog-posts', {
        id: `post-${Date.now()}`,
        slug,
        title: values.title ?? 'Untitled',
        excerpt: values.excerpt ?? '',
        content: values.content ?? '',
        image: values.image ?? '',
        author: values.author ?? '',
        publishedAt: values.publishedAt ?? new Date().toISOString().slice(0, 10),
        tags: values.tags ?? [],
      })
    },
    [],
  )

  return (
    <main>
      <Section tone="surface" className="py-12 sm:py-16">
        <Container>
          <EditablePageHeader page="blog" />
        </Container>
      </Section>

      <Section tone="low">
        <Container>
          {posts.length === 0 && !isEditMode ? (
            <p className="py-6 text-center text-on-surface-variant">
              No posts yet — check back soon for stories, recipes, and fresh-from-the-oven updates.
            </p>
          ) : (
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post, i) => (
                <BlurFade key={post.id} delay={0.05 * i}>
                  <BlogCard post={post} />
                </BlurFade>
              ))}
            </div>
          )}

          {isEditMode && (
            <div className="mt-10 flex justify-center">
              <button
                onClick={() => setAddOpen(true)}
                className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-md transition-transform hover:scale-105"
              >
                <Plus className="size-4" />
                Add Blog Post
              </button>
              <EditModal
                open={addOpen}
                onOpenChange={setAddOpen}
                title="Add Blog Post"
                fields={newPostFields}
                values={{
                  title: '',
                  excerpt: '',
                  author: '',
                  publishedAt: new Date().toISOString().slice(0, 10),
                  tags: [],
                  image: '',
                  content: '',
                }}
                onSave={handleAdd}
              />
            </div>
          )}
        </Container>
      </Section>
    </main>
  )
}
